<template>
  <section class="general-configuration__billing-data role-session-policies">
    <div class="general-configuration__section-header gap-6">
      <div>
        <h1>Políticas de sesión</h1>
        <p class="general-configuration__section-subtitle">
          Administra las políticas de inicio de sesión para controlar el acceso y la seguridad del usuario.
        </p>
      </div>
      <button
        class="button is-intelligential button-blue"
        type="button"
        :disabled="loading || saving"
        @click="openModal"
      >
        Editar
      </button>
    </div>

    <div class="columns is-multiline mb-0 mx-1">
      <div class="column is-one-quarter">
        <span class="label">
          Tiempo de inactividad
          <span
            class="has-tooltip-bottom has-tooltip-multiline"
            :data-tooltip="inactivityTooltip"
          >
            <i class="fas fa-info-circle"></i>
          </span>
        </span>
        <p class="text-value">{{ inactivityTimeLabel }}</p>
      </div>
      <div class="column is-one-quarter">
        <span class="label">
          Sesión única
          <span
            class="has-tooltip-bottom has-tooltip-multiline"
            :data-tooltip="singleSessionTooltip"
          >
            <i class="fas fa-info-circle"></i>
          </span>
        </span>
        <p class="text-value">{{ normalizedConfig.singleSession ? 'Sí' : 'No' }}</p>
      </div>
    </div>

    <modal-component
      :active="showModal"
      titleModal="Editar políticas de sesión"
      textSave="Guardar"
      :scrollBody="false"
      :isOverflow="true"
      :loader="saving"
      :disabled="!isFormValid"
      @cancel="closeModal"
      @hdlOnClick="save"
    >
      <div class="columns is-multiline mb-0">
        <div class="column is-6">
          <label class="label -text2 -gray">
            Tiempo de inactividad
            <span
              class="has-tooltip-bottom has-tooltip-multiline"
              :data-tooltip="inactivityTooltip"
            >
              <i class="fas fa-info-circle"></i>
            </span>
          </label>
          <div class="control has-icons-right">
            <input
              v-model.number="form.inactivityTimeMinutes"
              class="input inactivity-time-input"
              type="text"
              inputmode="numeric"
              maxlength="3"
              name="inactivityTimeMinutes"
              v-validate="'numeric|min_value:0|max_value:270'"
              data-vv-as="tiempo de inactividad"
              :class="{ 'is-danger': vverrors.has('inactivityTimeMinutes') }"
              @keypress="onlyNumbers($event)"
            >
            <span class="icon is-right inactivity-time-unit">minutos</span>
          </div>
          <p class="help is-danger">{{ vverrors.first('inactivityTimeMinutes') }}</p>
        </div>
        <div class="column is-6">
          <label class="label -text2 -gray">
            Sesión única
            <span
              class="has-tooltip-bottom has-tooltip-multiline"
              :data-tooltip="singleSessionTooltip"
            >
              <i class="fas fa-info-circle"></i>
            </span>
          </label>
          <div class="select is-fullwidth">
            <select v-model="form.singleSession">
              <option :value="true">Sí</option>
              <option :value="false">No</option>
            </select>
          </div>
        </div>
      </div>
    </modal-component>
  </section>
</template>

<script>
import validators from '@/mixins/validators'

const DEFAULT_CONFIG = {
  inactivityTimeMinutes: 5,
  singleSession: false
}

export default {
  name: 'RoleSessionPolicies',
  mixins: [validators],
  components: {
    ModalComponent: () => import('@/components/ui/ModalComponent.vue')
  },
  props: {
    role: {
      type: String,
      default: ''
    },
    roleSessionConfig: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showModal: false,
      form: {
        ...DEFAULT_CONFIG
      }
    }
  },
  computed: {
    normalizedConfig () {
      return {
        ...DEFAULT_CONFIG,
        ...(this.roleSessionConfig || {})
      }
    },
    inactivityTimeLabel () {
      const minutes = this.normalizedConfig.inactivityTimeMinutes
      return minutes == null || Number(minutes) === 0
        ? 'Desactivado'
        : `${minutes} minutos`
    },
    roleSessionExpiration () {
      switch (this.role) {
        case 'admin': return '4h'
        case 'manager': return '6h'
        case 'compliance': return '2h'
        default: return '8h'
      }
    },
    inactivityTooltip () {
      return `Define el tiempo de inactividad antes de cerrar la sesión. Si lo dejas vacío, la sesión cerrará automáticamente después de ${this.roleSessionExpiration}.`
    },
    singleSessionTooltip () {
      return 'Con sesión única activa, el usuario no puede estar conectado en más de un dispositivo al mismo tiempo.'
    },
    isFormValid () {
      const value = this.form.inactivityTimeMinutes
      const isEmpty = value === '' || value == null
      const minutes = Number(value)
      const isValidInactivityTime = isEmpty ||
        (Number.isInteger(minutes) && minutes >= 0 && minutes <= 270)

      return isValidInactivityTime &&
        typeof this.form.singleSession === 'boolean'
    }
  },
  methods: {
    openModal () {
      this.form = { ...this.normalizedConfig }
      this.showModal = true
    },
    closeModal () {
      if (this.saving) return
      this.showModal = false
    },
    async save () {
      if (!this.isFormValid) return
      const value = this.form.inactivityTimeMinutes
      const inactivityTimeMinutes = value === '' || value == null
        ? null
        : Number(value)

      this.$emit('save', {
        inactivityTimeMinutes,
        singleSession: this.form.singleSession
      }, success => {
        if (success) {
          this.showModal = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 15px;
  font-weight: 600;
  color: #4a4a4a;
}

.gap-6 {
  gap: 6px;
}

.control.has-icons-right .inactivity-time-input {
  padding-right: 4.75rem;
}

.control.has-icons-right .inactivity-time-unit {
  width: 4.25rem;
  height: 40px;
  top: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 12px;
  line-height: 1;
  pointer-events: none;
}

.general-configuration__section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 1.25rem;
  padding: 0 0.75rem;
}

.general-configuration__section-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}

.general-configuration__billing-data {
  padding: 0;
  margin-top: 2.25rem;

  .columns {
    padding: 0 0.75rem;
  }

  .label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 6px;
    display: block;
  }

  .text-value {
    font-size: 12px;
    font-weight: 500;
    color: #111827;
    margin: 0;
  }
}
</style>
